/**
 * Gallery Configuration System
 * ============================
 * 
 * Easy management of gallery URLs via Vercel environment variables.
 * 
 * HOW TO USE:
 * -----------
 * 1. Go to Vercel Dashboard → Project Settings → Environment Variables
 * 2. Add the following variables (comma-separated lists):
 * 
 *    VITE_GALLERY_VIDEOS = playbackId1,playbackId2,playbackId3
 *    VITE_GALLERY_PHOTOS = https://url1.jpg,https://url2.jpg,https://url3.jpg
 * 
 * 3. Redeploy your project
 * 
 * EXAMPLE WITH MUX VIDEOS:
 * ------------------------
 * VITE_GALLERY_VIDEOS=55af92c0a6afac58e780413127c73c5c,27cbea077c11d9cc0569774b8c443a04
 * 
 * EXAMPLE WITH CLOUDINARY (RECOMMENDED):
 * ---------------------------------------
 * VITE_GALLERY_PHOTOS=https://res.cloudinary.com/nm-creators/image/upload/...
 *
 * NOTES:
 * ------
 * - Videos use Mux playback IDs (the thumbnail is auto-generated)
 * - Photos: Cloudinary, Unsplash, o cualquier URL pública
 * - No se usan fotos de Cloudflare (imagedelivery.net) en la galería
 * - If env vars are empty, Index.tsx usa portfolio / site settings (admin)
 */

import type { VideoActionButton } from "@/components/MuxVideoPlayer";

export interface GalleryMediaItem {
  id: number;
  type: "video" | "photo";
  src: string;
  playbackId?: string;
  title: string;
  actionButton?: VideoActionButton;
}

/**
 * Parse comma-separated values from env var
 */
function parseEnvList(envValue: string | undefined): string[] {
  if (!envValue || typeof envValue !== "string") return [];
  return envValue
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * Get gallery videos from environment variable
 * Format: comma-separated Mux playback IDs
 * 
 * Example: VITE_GALLERY_VIDEOS=playbackId1,playbackId2,playbackId3
 */
export function getGalleryVideos(): GalleryMediaItem[] {
  const playbackIds = parseEnvList(import.meta.env.VITE_GALLERY_VIDEOS as string);
  
  if (playbackIds.length === 0) {
    return [];
  }

  return playbackIds.map((playbackId, index) => ({
    id: index + 1,
    type: "video" as const,
    src: `https://image.mux.com/${playbackId}/thumbnail.jpg`,
    playbackId,
    title: "",
  }));
}

/**
 * Get gallery photos from environment variable
 * Format: comma-separated image URLs
 *
 * Supports: Cloudinary, Unsplash, o cualquier URL pública.
 * No se usan fotos de Cloudflare (imagedelivery.net) en la galería.
 */
export function getGalleryPhotos(): GalleryMediaItem[] {
  const photoUrls = parseEnvList(import.meta.env.VITE_GALLERY_PHOTOS as string)
    .filter((url) => !url.includes("imagedelivery.net"));

  if (photoUrls.length === 0) {
    return [];
  }

  return photoUrls.map((url, index) => ({
    id: 1000 + index,
    type: "photo" as const,
    src: url,
    title: "Gallery",
  }));
}

/**
 * Get all gallery media from environment variables
 * Combines videos and photos, interleaves them for visual variety
 * 
 * Returns empty array if no env vars are set (falls back to hardcoded in Index.tsx)
 */
export function getGalleryMedia(): GalleryMediaItem[] {
  const videos = getGalleryVideos();
  const photos = getGalleryPhotos();
  
  // If no env vars set, return empty to signal fallback
  if (videos.length === 0 && photos.length === 0) {
    return [];
  }
  
  // Interleave videos and photos for visual variety
  // Pattern: video, photo, photo, video, photo, photo, ...
  const result: GalleryMediaItem[] = [];
  let videoIdx = 0;
  let photoIdx = 0;
  let idCounter = 1;
  
  while (videoIdx < videos.length || photoIdx < photos.length) {
    // Add a video
    if (videoIdx < videos.length) {
      result.push({ ...videos[videoIdx], id: idCounter++ });
      videoIdx++;
    }
    
    // Add 2 photos
    for (let i = 0; i < 2 && photoIdx < photos.length; i++) {
      result.push({ ...photos[photoIdx], id: idCounter++ });
      photoIdx++;
    }
  }
  
  return result;
}

/**
 * Check if gallery is configured via environment variables
 */
export function isGalleryConfigured(): boolean {
  const hasVideos = parseEnvList(import.meta.env.VITE_GALLERY_VIDEOS as string).length > 0;
  const hasPhotos = parseEnvList(import.meta.env.VITE_GALLERY_PHOTOS as string).length > 0;
  return hasVideos || hasPhotos;
}

// ====================================
// DEFAULT FALLBACK VALUES
// ====================================
// These are used when no env vars are configured
// You can modify these as a quick fallback

export const DEFAULT_GALLERY_VIDEOS = [
  "55af92c0a6afac58e780413127c73c5c",
  "27cbea077c11d9cc0569774b8c443a04",
  "0599f80309c4103f7ebe835cf09f5a41",
  "DyyJ3idqFVxOW9Mtj9MUUhRpS19WqdWTmxeJyvntULw",
  "ANe9AEYt1jHyuBLSN1SVDoTah727CCoNMffDvUc005RM",
];

/** No hay defaults de Cloudflare - la galería usa Cloudinary, Mux o assets locales */
export const DEFAULT_GALLERY_PHOTOS: string[] = [];
